import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIResponse } from "@/types/custom-http";
import { ExternalOts, ExternalOtsState } from "./external-ots.interface";
import * as thunks from "./external-otsThunks";

const initialState: ExternalOtsState = {
  items: [],
  selectedExternalOts: null,
  loading: false,
  error: null,
};

const externalOtsSlice = createSlice({
  name: "externalOts",
  initialState,
  reducers: {
    clearExternalOts: (state) => {
      state.items = [];
    },
    clearSelectedExternalOts: (state) => {
      state.selectedExternalOts = null;
    },
    clearExternalOtsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.fetchExternalOts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchExternalOts.fulfilled, (state, action: PayloadAction<APIResponse<ExternalOts[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items = action.payload.data;
        }
      })
      .addCase(thunks.fetchExternalOts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.searchExternalOts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.searchExternalOts.fulfilled, (state, action: PayloadAction<APIResponse<ExternalOts[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items = action.payload.data;
        }
      })
      .addCase(thunks.searchExternalOts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchExternalOtsById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchExternalOtsById.fulfilled, (state, action: PayloadAction<APIResponse<ExternalOts>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.selectedExternalOts = action.payload.data;
        }
      })
      .addCase(thunks.fetchExternalOtsById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.updateExternalOts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateExternalOts.fulfilled, (state, action: PayloadAction<APIResponse<ExternalOts>>) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedExternalOts && state.selectedExternalOts.id === action.payload.data?.id) {
          state.selectedExternalOts = action.payload.data;
        }
      })
      .addCase(thunks.updateExternalOts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      });
  },
});

export const selectAllExternalOts = (state: RootState) => state.externalOts.items;
export const selectExternalOtsById = (state: RootState, id: number) =>
  state.externalOts.items.find((item) => item.id === id);
export const selectSelectedExternalOts = (state: RootState) => state.externalOts.selectedExternalOts;
export const selectExternalOtsLoading = (state: RootState) => state.externalOts.loading;
export const selectExternalOtsError = (state: RootState) => state.externalOts.error;

export const { clearExternalOts, clearSelectedExternalOts, clearExternalOtsError } = externalOtsSlice.actions;

export default externalOtsSlice.reducer;
